import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  FaDownload,
  FaCode,
  FaServer,
  FaDatabase,
  FaPalette,
} from "react-icons/fa";
import "./About.css";
import cv from "../../assets/cv.pdf";
import damian from "../../assets/king.PNG";
import { Helmet } from "react-helmet-async";

const skillsCategories = [
  {
    title: "Frontend Mastery",
    icon: <FaPalette />,
    skills: [
      "HTML5/CSS3 (Sass, Tailwind)",
      "JavaScript (ES6+)",
      "React & Next.js",
      "Responsive Design",
      "UI/UX Principles",
    ],
    color: "#61DAFB",
  },
  {
    title: "Backend Expertise",
    icon: <FaServer />,
    skills: [
      "Node.js & Express",
      "RESTful API Design",
      "Authentication (JWT)",
      "Server-side Rendering",
      "Web Security",
    ],
    color: "#68A063",
  },
  {
    title: "Database Skills",
    icon: <FaDatabase />,
    skills: [
      "MongoDB (Mongoose)",
      "MySQL & PostgreSQL",
      "Database Optimization",
      "ORM/ODM Experience",
      "Data Modeling",
    ],
    color: "#47A248",
  },
  {
    title: "Full Stack",
    icon: <FaCode />,
    skills: [
      "MERN Stack Pro",
      "State Management",
      "Microservices",
      "DevOps Basics",
      "Testing (Jest)",
    ],
    color: "#764ABC",
  },
];

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <div>
      <Helmet>
        <title>About Me - King Damian</title>
        <meta
          name="description"
          content="All you need to know about King Damian"
        />
        <meta property="og:title" content="About Me - King Damian" />
        <meta
          property="og:description"
          content="All you need to know about King Damian"
        />
      </Helmet>
      <motion.div
        className="about-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="about-hero">
          <div className="about-content" ref={ref}>
            <motion.div className="image-container">
              <motion.img
                src={damian}
                alt="King Damian"
                className="profile-image"
                whileHover={{ scale: 1.03 }}
              />
            </motion.div>

            <div className="text-content">
              <motion.h1
                initial={{ x: -20, opacity: 0 }}
                animate={isInView ? { x: 0, opacity: 1 } : {}}
                transition={{ delay: 0.2 }}
              >
                About <span className="highlight">Me</span>
              </motion.h1>

              <motion.p
                initial={{ x: -20, opacity: 0 }}
                animate={isInView ? { x: 0, opacity: 1 } : {}}
                transition={{ delay: 0.3 }}
              >
                {" "}
                My name is King Damian, a passionate and dedicated{" "}
                <span className="accent-text">Computer Scientist</span> & Full
                Stack Developer. I specialize in web development, mobile app
                development, AI Integration, and cloud computing. I am committed
                to creating innovative solutions that enhance user experiences
                and drive business success. I'm always eager to learn and adapt
                to new technologies, ensuring that I stay at the forefront of
                the ever-evolving tech landscape. My goal is to leverage my
                skills and knowledge to contribute to impactful projects and
                make a positive difference in the world through technology. I'm
                also dedicated in owning one of the biggest tech institution in
                the country.
              </motion.p>

              <motion.a
                href={cv}
                download
                className="download-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaDownload /> Download CV
              </motion.a>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section className="skills-matrix">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
          >
            My <span className="highlight">Technical</span> Toolkit
          </motion.h2>

          <div className="skills-grid">
            {skillsCategories.map((category, index) => (
              <motion.div
                key={index}
                className="skill-category"
                initial={{ y: 30, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ delay: 0.2 + index * 0.1 }}
                style={{ borderTop: `4px solid ${category.color}` }}
              >
                <div
                  className="category-header"
                  style={{ color: category.color }}
                >
                  {category.icon}
                  <h3>{category.title}</h3>
                </div>
                <ul>
                  {category.skills.map((skill, i) => (
                    <motion.li
                      key={i}
                      initial={{ x: -10, opacity: 0 }}
                      animate={isInView ? { x: 0, opacity: 1 } : {}}
                      transition={{ delay: 0.3 + index * 0.1 + i * 0.05 }}
                    >
                      {skill}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Developer Philosophy */}
        <section className="philosophy">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
          >
            My <span className="highlight">Development</span> Philosophy
          </motion.h2>

          <motion.div className="philosophy-cards">
            {[
              {
                title: "User-Centric Design",
                text: "Technology should adapt to humans, not the other way around. Every pixel and interaction is crafted with the end-user in mind.",
              },
              {
                title: "Clean Code Matters",
                text: "Code is read more often than it's written. I prioritize maintainable, well-documented code that stands the test of time.",
              },
              {
                title: "Continuous Growth",
                text: "In our fast-moving industry, stagnation is regression. I dedicate time weekly to learn new technologies and refine my craft.",
              },
            ].map((philosophy, index) => (
              <motion.div
                key={index}
                className="philosophy-card"
                whileHover={{ y: -10 }}
              >
                <h3>{philosophy.title}</h3>
                <p>{philosophy.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>
      </motion.div>
    </div>
  );
};

export default About;
